/**
 * Runtime capability adapter for the desktop shell (WP7.8b, DECISIONS.md ADR-085 Amendment).
 *
 * Pages and composables ask THIS module "am I in the desktop shell, and what is this workstation
 * configured with?" — they never touch `@tauri-apps/api` (only `desktop-bridge.ts` does) and never
 * sprinkle `window.__TAURI__` checks. In a plain browser tab every capability is simply absent
 * (`isDesktop()` is `false`), so the same web build keeps working unchanged.
 *
 * Per-workstation settings (which printer, which scanner port) are stored locally in the browser
 * storage of the desktop webview: they describe *this machine's* hardware, so they are deliberately
 * neither tenant data nor synced through the server.
 */
import { connectScanner, isDesktopRuntime, printZpl, type PrinterTarget, type PrintResult } from './desktop-bridge.js';

/** True only inside the Tauri desktop shell. */
export function isDesktop(): boolean {
  return isDesktopRuntime();
}

/** Minimal storage surface (a `localStorage` subset) so tests can inject a fake and a blocked/absent
 * storage degrades to "not configured" instead of throwing. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const PRINTER_KEY = 'jervis.desktop.printer';
const SCANNER_KEY = 'jervis.desktop.scanner';

/** Default raw-print port of Zebra-compatible network label printers (JetDirect). */
export const DEFAULT_NETWORK_PRINTER_PORT = 9100;

/** Validation error codes are i18n-mapped by the settings screen (`desktop.settings.printer.errors.*`). */
export type PrinterTargetError = 'hostRequired' | 'portInvalid' | 'printerNameRequired';

export function validatePrinterTarget(target: PrinterTarget): PrinterTargetError | null {
  if (target.kind === 'network') {
    if (!target.host.trim()) return 'hostRequired';
    if (!Number.isInteger(target.port) || target.port < 1 || target.port > 65535) return 'portInvalid';
    return null;
  }
  return target.printerName.trim() ? null : 'printerNameRequired';
}

function isPrinterTarget(value: unknown): value is PrinterTarget {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v['kind'] === 'network') return typeof v['host'] === 'string' && typeof v['port'] === 'number';
  if (v['kind'] === 'windowsUsb') return typeof v['printerName'] === 'string';
  return false;
}

/** The label printer chosen on this workstation, or `null` if none (or storage unreadable/corrupt). */
export function loadPrinterConfig(storage: SettingsStorage | null = defaultStorage()): PrinterTarget | null {
  try {
    const raw = storage?.getItem(PRINTER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPrinterTarget(parsed) && validatePrinterTarget(parsed) === null ? parsed : null;
  } catch {
    return null;
  }
}

/** Persists the printer choice; throws on an invalid target so a bad config is never stored. */
export function savePrinterConfig(target: PrinterTarget, storage: SettingsStorage | null = defaultStorage()): void {
  const problem = validatePrinterTarget(target);
  if (problem) throw new Error(`Invalid printer configuration: ${problem}`);
  storage?.setItem(PRINTER_KEY, JSON.stringify(target));
}

export function clearPrinterConfig(storage: SettingsStorage | null = defaultStorage()): void {
  storage?.removeItem(PRINTER_KEY);
}

export class PrinterNotConfiguredError extends Error {
  constructor() {
    super('No desktop label printer is configured on this workstation.');
    this.name = 'PrinterNotConfiguredError';
  }
}

/**
 * Sends server-rendered ZPL (the `POST /printing/print` `content`, ADR-084) to this workstation's
 * configured label printer through the Tauri printer command. Never invents a printer: with none
 * configured it throws {@link PrinterNotConfiguredError} so the UI can point at the settings screen.
 */
export async function printToDesktopPrinter(
  zpl: string,
  storage: SettingsStorage | null = defaultStorage(),
): Promise<PrintResult> {
  const target = loadPrinterConfig(storage);
  if (!target) throw new PrinterNotConfiguredError();
  return printZpl(target, zpl);
}

export interface ScannerConfig {
  portName: string;
  baudRate: number;
}

export const DEFAULT_SCANNER_BAUD_RATE = 9600;

export function loadScannerConfig(storage: SettingsStorage | null = defaultStorage()): ScannerConfig | null {
  try {
    const raw = storage?.getItem(SCANNER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScannerConfig> | null;
    if (
      parsed &&
      typeof parsed.portName === 'string' &&
      parsed.portName &&
      typeof parsed.baudRate === 'number' &&
      Number.isInteger(parsed.baudRate) &&
      parsed.baudRate > 0
    ) {
      return { portName: parsed.portName, baudRate: parsed.baudRate };
    }
    return null;
  } catch {
    return null;
  }
}

export function saveScannerConfig(config: ScannerConfig, storage: SettingsStorage | null = defaultStorage()): void {
  if (!config.portName.trim() || !Number.isInteger(config.baudRate) || config.baudRate <= 0) {
    throw new Error('Invalid scanner configuration');
  }
  storage?.setItem(SCANNER_KEY, JSON.stringify(config));
}

export function clearScannerConfig(storage: SettingsStorage | null = defaultStorage()): void {
  storage?.removeItem(SCANNER_KEY);
}

/** Re-opens the serial scanner chosen earlier on this workstation (app start). Never throws: an
 * unplugged scanner must not break app start-up — the settings screen shows the connection state. */
export async function restoreScanner(storage: SettingsStorage | null = defaultStorage()): Promise<boolean> {
  if (!isDesktop()) return false;
  const config = loadScannerConfig(storage);
  if (!config) return false;
  try {
    await connectScanner(config.portName, config.baudRate);
    return true;
  } catch {
    return false;
  }
}
